import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { isEmpty } from 'lodash'

import { AiSparkles } from '@/components/ai/AiSparkles'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import {
  formatTime,
  getOrganizationOptions,
  getHierarchyOrganizationOptions,
  getChannelTypeOptions,
  hasAnyOmnichannelAccess,
} from '@/utils/helpers'
import {
  setOrganization,
  setDraftFilter,
  commitDraftFilters,
} from '@/reducers/qa-copilot/qa-copilot.redux'
import {
  fetchCopilotFilterData,
  fetchCopilotDashboardData,
  fetchInitialCopilotDashboardData,
  fetchVoipCampaignsByOrg,
} from '@/reducers/qa-copilot/qa-copilot.actions'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { WinsFilter } from '@/components/filters/WinsFilter'
import { MultiFilter } from '@/components/filters/MultiFilter'
import { NestedSelect } from '@/components/forms/NestedSelect'
import { PartialScoresFilter } from '@/components/filters/PartialScoresFilter'
import Select from '@/components/forms/Select'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { CHANNEL_TYPE } from '@/utils/constants'

const CopilotFiltersComponent = () => {
  const dispatch = useDispatch()
  const organizations = useSelector((state) => state.organizations)
  // The filter bar reads + writes the draft slice. Drafts only commit
  // to `filters` when the user clicks Apply, which is what kicks off
  // dashboard fetches. `data` and `loading` stay shared.
  const {
    data,
    draftFilters: filters,
    filters: appliedFilters,
    loading,
  } = useSelector((state) => state.qaCopilot)
  const isDirty = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
    [filters, appliedFilters]
  )
  const { organizationid: currentUserOrgId, hierarchy_manager } = useSelector(
    (state) => state.currentUser
  )
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const location = useLocation()
  const isBaltoAdmin = currentUserOrgId === 1

  const { search } = location
  const params = queryString.parse(search)

  const showManagerHierarchyOrgDropdown = !isBaltoAdmin && hierarchy_manager
  const hasOrganizationDropdown = isBaltoAdmin || showManagerHierarchyOrgDropdown
  const organizationOptions = useMemo(
    () => getOrganizationOptions(organizations, true),
    [organizations?.length]
  )
  const hierarchyOrganizationOptions = useMemo(
    () => getHierarchyOrganizationOptions(flattenedUserOrgHierarchy),
    [flattenedUserOrgHierarchy?.length]
  )

  const orgActiveChannels = useMemo(() => {
    if (isBaltoAdmin && filters.organizationId) {
      const selectedOrg = organizations?.find((org) => org.id === filters.organizationId)
      return selectedOrg?.active_channels || []
    }
    return activeChannels
  }, [isBaltoAdmin, filters.organizationId, organizations, activeChannels])

  const isOmniChannelEnabled = hasAnyOmnichannelAccess(orgActiveChannels)

  const handleUpdateRange = (range) => {
    const startDate = formatTime(range.selection.startDate, 'yyyy-MM-DDT00:00:00Z')
    const endDate = formatTime(range.selection.endDate, 'yyyy-MM-DDT23:59:59Z')

    dispatch(setDraftFilter({ startDate, endDate }))
  }

  const handleFilterChange = (value) => {
    dispatch(setDraftFilter(value))
  }

  const handleToggleDispositionsIncludes = (section) => {
    dispatch(
      setDraftFilter({
        [section]: {
          selected: filters[section].selected,
          includes: !filters[section].includes,
        },
      })
    )
  }

  const handleToggleDispositionsEntry = (selectedNode) => {
    const { value } = selectedNode
    const { selected = [], includes } = filters.dispositions

    const isAlreadySelected = selected.some((item) => item.value === value)

    const updatedSelected = isAlreadySelected
      ? selected.filter((item) =>
          typeof item === 'string' ? item !== value : item.value !== value
        )
      : [...selected, { value }]

    dispatch(
      setDraftFilter({
        dispositions: {
          selected: updatedSelected,
          includes,
        },
      })
    )
  }

  const handleClearDispositions = (section) => {
    dispatch(
      setDraftFilter({
        [section]: { selected: [], includes: filters[section].includes },
      })
    )
  }

  // Only these loaders should disable Apply. Filter-data loaders
  // (scorecards/agents/tags/playbooks/voipCampaigns/dispositions) are
  // independent of dashboard fetches — they shouldn't block re-applying
  // filters, and if one hangs after an org switch it would otherwise
  // perma-disable the button (Bug 1).
  const DASHBOARD_LOADING_KEYS = [
    'reporting',
    'reportingByUser',
    'reportingByDay',
    'scorecardPerformance',
    'nonCompliantCriteria',
    'nonCompliantAgents',
    'evaluatedCallData',
    'failedCallData',
    'failedCriteriaCallData',
    'topInfractors',
  ]

  const filterIsDisabled = () => {
    // Balto Admin and Hierarchy Manager require an organization to be selected
    if (hasOrganizationDropdown && !filters.organizationId) {
      return true
    }

    return DASHBOARD_LOADING_KEYS.some((key) => loading[key])
  }

  const handleOrgSelect = (option, action) => {
    if (action.action === 'clear') {
      dispatch(setOrganization(null))
    } else {
      dispatch(setOrganization(option.value))
      dispatch(fetchCopilotFilterData(option.value))
      // Auto-refetch dashboard data so the chart reflects the newly-selected
      // org immediately. Without this, the chart shows stale data from the
      // previously-applied org until the user clicks Apply Filters, which
      // makes click-to-drill interactions (e.g. ScoreTrends day click) appear
      // broken: the user clicks a day they think belongs to the new org, but
      // the filter narrows to that date for the new org and returns no data.
      dispatch(fetchCopilotDashboardData(option.value))
    }
  }

  useEffect(() => {
    const loadInitialData = async () => {
      // Default the org filter to the logged-in user's org when one
      // isn't already set (e.g. Balto admin lands on the page with no
      // URL params, no saved filter). Without this the page sits with
      // no scope and nothing fetches until the admin picks an org.
      // We mirror the Balto-admin-on-org-click path: load filter data
      // for the defaulted org + kick the dashboard fetch.
      const needsOrgDefault =
        hasOrganizationDropdown && !filters.organizationId && !params?.organization_id
      if (needsOrgDefault) {
        dispatch(setOrganization(currentUserOrgId))
        dispatch(fetchCopilotFilterData(currentUserOrgId))
        dispatch(fetchCopilotDashboardData(currentUserOrgId))
      }
      dispatch(fetchInitialCopilotDashboardData(hasOrganizationDropdown, currentUserOrgId, params))
    }

    loadInitialData()

    // when you leave this page, set this back to your user until the dropdown for hierarchy managers is everywhere
    return () => {
      if (showManagerHierarchyOrgDropdown && filters.organizationId !== currentUserOrgId) {
        dispatch(setOrganization(currentUserOrgId))
      }
    }
  }, [])

  useEffect(() => {
    if (filters.organizationId) {
      dispatch(fetchVoipCampaignsByOrg(filters.organizationId))
    }
  }, [filters.startDate, filters.endDate])

  const showWinsFilter = !filters.callType || filters.callType === CHANNEL_TYPE.CALL

  return (
    <div className="filter-form static-filter-form">
      <div>
        <div className="filter-grid">
          {hasOrganizationDropdown && (
            <OrganizationFilter
              dataTestId={
                showManagerHierarchyOrgDropdown
                  ? 'hierarchy-organization-dropdown'
                  : 'organization-dropdown'
              }
              options={
                showManagerHierarchyOrgDropdown ? hierarchyOrganizationOptions : organizationOptions
              }
              value={filters.organizationId}
              onChange={handleOrgSelect}
            />
          )}
          {isOmniChannelEnabled && (
            <FormField data-testid="channel-picker">
              <label>Channel</label>
              <Select
                onChange={(option) => {
                  dispatch(setDraftFilter({ callType: option.value }))
                }}
                isClearable={false}
                value={filters.callType}
                options={getChannelTypeOptions(orgActiveChannels)}
                fixedWidth
              />
            </FormField>
          )}
          <FormField>
            <label>Date Range</label>
            <DateRangePickerReusable
              data-testid="date-range-picker"
              onChange={handleUpdateRange}
              startDate={filters.startDate}
              endDate={filters.endDate}
            />
          </FormField>
          <MultiFilter
            label="Playbooks"
            accessor="playbooks"
            store={{ data, loading, filters }}
            handler={handleFilterChange}
          />
          <MultiFilter
            label="Scorecards"
            accessor="scorecards"
            store={{ data, loading, filters }}
            handler={handleFilterChange}
          />
          <MultiFilter
            label="Agents"
            accessor="agents"
            store={{ data, loading, filters }}
            handler={handleFilterChange}
            formatOptionLabel={(option) => {
              return (
                <div className="flex">
                  {option.label}
                  {option.isAi && <AiSparkles />}
                </div>
              )
            }}
          />
          <MultiFilter
            label="Tags"
            accessor="tags"
            store={{ data, loading, filters }}
            handler={handleFilterChange}
          />
          <MultiFilter
            label="VoIP Campaigns"
            accessor="voipCampaigns"
            store={{ data, loading, filters }}
            handler={handleFilterChange}
          />
          {showWinsFilter && (
            <WinsFilter
              filters={filters}
              onChange={(option, action) => {
                dispatch(setDraftFilter({ isWin: action.action === 'clear' ? null : option.value }))
              }}
            />
          )}
          <PartialScoresFilter
            filters={filters}
            onChange={(option, action) => {
              dispatch(
                setDraftFilter({ scoredStatus: action.action === 'clear' ? null : option.value })
              )
            }}
          />
          {!isEmpty(data.dispositions) && (
            <FormField data-testid="dispositions-dropdown">
              <label>Dispositions</label>
              <NestedSelect
                options={data.dispositions.map((dispo) => ({
                  ...dispo,
                  section: 'dispositions',
                  checked: filters.dispositions.selected?.some(
                    (selected) => selected.value === dispo.value
                  ),
                }))}
                value={filters.dispositions.selected}
                includes={filters.dispositions.includes}
                disabled={loading.dispositions}
                section="dispositions"
                toggleIncludes={handleToggleDispositionsIncludes}
                toggleEntry={handleToggleDispositionsEntry}
                clearAll={handleClearDispositions}
              />
            </FormField>
          )}
        </div>
      </div>
      <div className="filter-buttons">
        <FormField>
          <label className="visibility-hidden">&nbsp;</label>
          <div>
            <Button
              brand
              onClick={() => {
                // Commit drafts → filters first so the dashboard
                // fetches use the freshly-applied values. The fetch
                // pulls the org from drafts because drafts === filters
                // immediately after the commit.
                dispatch(commitDraftFilters())
                dispatch(fetchCopilotDashboardData(filters.organizationId || currentUserOrgId))
              }}
              disabled={filterIsDisabled()}
              disabledTooltip={isDirty ? '' : 'No filter changes to apply'}
              data-testid="submit-filters-button"
            >
              {isDirty ? 'Filter •' : 'Filter'}
            </Button>
          </div>
        </FormField>
      </div>
    </div>
  )
}

export const CopilotFilters = CopilotFiltersComponent
